/**
 * POD Add to Cart Function
 * 
 * This module provides functionality to add 3D print jobs to the cart.
 * It exports a single async function that can be called when needed.
 */

// Shopify Helper utility for better organization and error handling
const ShopifyHelper = {
  /**
   * Get the cart endpoint URL with fallback for different Shopify setups
   * @returns {string} The cart endpoint URL
   */
  getCartEndpoint: function() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart/add.js';
    } else {
      return '/cart/add.js';
    }
  },

  /**
   * Add items to cart using Shopify's cart API
   * @param {Object} formData - The cart data to submit
   * @returns {Promise<Object>} Promise resolving to cart addition result
   */
  addToCart: async function(formData) {
    const cartEndpoint = this.getCartEndpoint();
    
    try {
      const response = await fetch(cartEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Cart API returned status ${response.status}: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Navigate to cart page
   */
  goToCart: function() {
    window.location.href = '/cart';
  }
};

// Function to add a print to cart
async function addPrintToCart(printDetails, fileId) {
    if (!printDetails || !fileId) {
      throw new Error('Missing required details for cart addition');
    }
    
    try {
      const quantity = getQuantityValue();
      const itemTitle = formatItemTitle(printDetails);
      const propertiesAdjustment = createItemProperties(printDetails, fileId);
      const propertiesBase = 'Base Fee';
      
      const result = await sendToCart(itemTitle, propertiesBase, propertiesAdjustment, printDetails.price_adjustment, quantity);
      return result;
    } catch (error) {
      throw error;
    }
  }
  
  // Navigate to cart page
  function goToCart() {
    ShopifyHelper.goToCart();
  }
  
  /**
   * Get the current quantity value from the input
   * @returns {number} - The quantity value (defaults to 1 if not found)
   */
  function getQuantityValue() {
    const quantityInput = document.getElementById('item-quantity');
    if (quantityInput) {
      const qty = parseInt(quantityInput.value, 10);
      return isNaN(qty) || qty < 1 ? 1 : qty;
    }
    return 1;
  }
  
  /**
   * Format the main item title based on print details
   * @param {Object} printDetails - The print details
   * @returns {string} - Formatted title
   */
  function formatItemTitle(printDetails) {
    let title = `3D Print - ${printDetails.material.toUpperCase()} ${printDetails.color}`;
    
    switch (printDetails.quality) {
      case 'high':
        title += ' - Quality Print';
        break;
      case 'medium':
        title += ' - Standard Print';
        break;
      case 'low':
        title += ' - Draft Print';
        break;
    }
    
    return title;
  }
  
  /**
   * Create line item properties for the cart
   * @param {Object} printDetails - The print details
   * @param {string} fileId - The ID of the uploaded file
   * @returns {Object} - Properties object for the cart
   */
  function createItemProperties(printDetails, fileId) {
    let printDetailText = '';
    switch (printDetails.quality) {
      case 'high':
        printDetailText = 'Quality Print';
        break;
      case 'medium':
        printDetailText = 'Standard Print';
        break;
      case 'low':
        printDetailText = 'Draft Print';
        break;
      default:
        printDetailText = printDetails.quality;
    }
    
    const supportsValue = printDetails.supports.charAt(0).toUpperCase() + 
                          printDetails.supports.slice(1).toLowerCase();
    
    return {
      'Material': `${printDetails.material.toUpperCase()} ${printDetails.color}`,
      'Print Detail': printDetailText,
      'Infill': `${printDetails.infill}%`,
      'Supports': supportsValue,
      'Dimensions': printDetails.formattedDimensions,
      'Print Weight': `${printDetails.weight}g`,
      'File ID': fileId
    };
  }
  
  /**
   * Send the item to the cart
   * @param {string} title - The item title
   * @param {string} propertiesBase - The base item property text
   * @param {Object} propertiesAdjustment - The price adjustment item properties
   * @param {number} priceAdjustment - The price adjustment value
   * @param {number} quantity - The quantity to add (defaults to 1)
   * @returns {Promise<Object>} - Promise resolving to cart addition result
   */
  async function sendToCart(title, propertiesBase, propertiesAdjustment, priceAdjustment, quantity = 1) {
    const variantId = 46496568377582;
    const priceAdjustmentVariantId = 46496568410350;
    
    const adjustmentQty = Math.max(1, Math.abs(priceAdjustment || 1));
    
    let formData = {
      'items': [
        {
          'id': variantId,
          'quantity': quantity,
          'properties': {
            '': propertiesBase
          }
        },
        {
          'id': priceAdjustmentVariantId,
          'quantity': adjustmentQty * quantity,
          'properties': propertiesAdjustment,
          'properties_title': 'Price Adjustment'
        }
      ]
    };
    
    return await ShopifyHelper.addToCart(formData);
  }
  
  // Export the functions directly
  window.addPrintToCart = addPrintToCart;
  window.goToCart = goToCart;

// Check for the size limitation before adding to cart
const originalAddPrintToCart = addPrintToCart;
addPrintToCart = function(printDetails, fileId) {
  if (window.sizeExceeded) {
    alert('This model exceeds the maximum print dimensions of 250mm. Please scale it down before adding to cart.');
    return Promise.reject('Model size exceeds limits');
  }
  
  return originalAddPrintToCart(printDetails, fileId);
};

// Make sure the modified function is also exported to window
window.addPrintToCart = addPrintToCart;